import {
  listChats,
  listChatMessages,
  listJoinedTeams,
  listChannels,
  listChannelMessages,
  chatDisplayName,
  conversationKind,
  getMe,
} from './graph/chats.mjs';
import {
  isDownloadable,
  downloadAttachment,
  downloadHostedContent,
  extractHostedContentIds,
  rewriteBodyHtml,
} from './graph/attachments.mjs';
import { classifyEventMessage, downloadRecording, tryDownloadTranscriptForRecording } from './graph/meetings.mjs';
import { slugify, safeFilename, shortId } from './util/slug.mjs';

const defaultLogger = {
  info: (m) => console.log(m),
  warn: (m) => console.warn(`WARN: ${m}`),
  error: (m) => console.error(`ERROR: ${m}`),
};

export async function exportAll({
  graph,
  sink,
  options = {},
  logger = defaultLogger,
  filter = null,
  chatQuery = {},   // { since, until } — pushed to Graph as a $filter
  onProgress = null,
  // Incremental mode: pass the manifest loaded from the existing archive.
  // Each chat already in the manifest is fetched from its last-known
  // timestamp forward and merged; chats not in the manifest are exported in
  // full; archived chats not in the current Graph enumeration are preserved
  // untouched (additive-only semantics).
  existingManifest = null,
  // Cooperative cancellation. Checked between chats — the current chat's
  // messages/attachments finish downloading, then we bail out cleanly.
  signal = null,
}) {
  const isAborted = () => !!(signal && signal.aborted);
  const opts = {
    downloadAttachments: true,
    downloadInlineImages: true,
    downloadRecordings: true,
    downloadTranscripts: true,
    includeChannels: false,
    concurrency: 6,
    ...options,
  };
  const isIncremental = !!existingManifest;
  const existingById = new Map((existingManifest?.conversations || []).map((c) => [c.id, c]));

  await sink.init();

  const me = await getMe(graph).catch(() => null);
  const meId = me?.id || null;
  logger.info(`Signed in as ${me?.userPrincipalName || me?.displayName || 'unknown user'}`);

  const manifest = [];
  // Counters for the end-of-run summary. Permission-denied outcomes are
  // expected (files you can't access, meeting chats you're not on the roster
  // for) — we track them but keep them at info-level in the live log so they
  // don't look like failures.
  const stats = { skippedChats403: 0, skippedAttachments403: 0 };

  logger.info('Enumerating chats...');
  const chats = [];
  let enumerated = 0;
  for await (const chat of listChats(graph, chatQuery)) {
    chats.push(chat);
    enumerated += 1;
    if (enumerated % 50 === 0) logger.info(`  loaded ${enumerated} so far…`);
  }
  logger.info(`Found ${chats.length} chats.`);

  const targets = chats
    .map((chat) => ({ chat, name: chatDisplayName(chat, meId), kind: conversationKind(chat) }))
    .filter((t) => !filter || filter(t.chat, t.name));

  logger.info(`Exporting ${targets.length} conversations (${opts.concurrency} in parallel)...`);
  let done = 0;
  const total = targets.length;

  await pool(targets, opts.concurrency, async (t, i) => {
    if (isAborted()) return; // stop taking new work
    const existing = existingById.get(t.chat.id);
    const label = existing ? 'incremental' : (isIncremental ? 'new' : t.kind);
    logger.info(`[${i + 1}/${total}] ${label}: ${t.name}`);
    try {
      const entry = await exportOneChat({
        graph, sink, chat: t.chat, kind: t.kind, name: t.name, opts, logger, stats,
        existingEntry: existing,
      });
      if (entry) manifest.push(entry);
    } catch (err) {
      if (err.status === 403) {
        stats.skippedChats403 += 1;
        logger.info(`  skipped "${t.name}" (no access to this chat's messages)`);
      } else {
        logger.error(`  failed to export "${t.name}" (${t.chat.id}): ${err.message}`);
      }
    } finally {
      done += 1;
      if (onProgress) onProgress({ done, total, current: t.name });
    }
  });

  // Additive-only: carry over any conversations that were in the existing
  // manifest but aren't visible via Graph on this run (chats the user left,
  // meetings that have aged out of /me/chats, etc.).
  if (isIncremental) {
    const seen = new Set(manifest.map((e) => e.id));
    for (const carried of existingManifest.conversations) {
      if (!seen.has(carried.id)) manifest.push(carried);
    }
  }

  if (opts.includeChannels) {
    logger.info('Enumerating teams + channels...');
    for await (const team of listJoinedTeams(graph)) {
      const teamSlug = slugify(team.displayName || team.id);
      for await (const channel of listChannels(graph, team.id)) {
        const chName = channel.displayName || channel.id;
        logger.info(`  channel: ${team.displayName} › ${chName}`);
        try {
          const entry = await exportOneChannel({ graph, sink, team, teamSlug, channel, opts, logger });
          if (entry) manifest.push(entry);
        } catch (err) {
          if (err.status === 403) {
            logger.warn(`    skipped (admin consent required for ChannelMessage.Read.All)`);
          } else {
            logger.error(`    failed: ${err.message}`);
          }
        }
      }
    }
  }

  manifest.sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''));
  await sink.finalizeManifest(manifest);
  if (isAborted()) {
    logger.info(`\nStopped. Saved partial archive with ${manifest.length} conversations. Anything unfinished can be picked up with another Update backup.`);
  } else {
    logger.info(`\nDone. Exported ${manifest.length} conversations.`);
  }
  if (stats.skippedChats403 || stats.skippedAttachments403) {
    const parts = [];
    if (stats.skippedChats403) parts.push(`${stats.skippedChats403} chat${stats.skippedChats403 === 1 ? '' : 's'}`);
    if (stats.skippedAttachments403) parts.push(`${stats.skippedAttachments403} attachment${stats.skippedAttachments403 === 1 ? '' : 's'}`);
    logger.info(`Skipped ${parts.join(' and ')} due to permissions (these are files or chats you don't have access to — expected, not an error).`);
  }
  return manifest;
}

async function exportOneChat({ graph, sink, chat, kind, name, opts, logger, stats, existingEntry = null }) {
  // Incremental: reuse the existing directory + load the archived messages.
  const handle = await sink.openConversation({
    kind, id: chat.id, name, chat,
    existingRelDir: existingEntry?.relDir || null,
  });

  let existingMessages = [];
  let sinceTimestamp = null;
  if (existingEntry) {
    try {
      const prior = await sink.readMessagesJson(existingEntry.relDir);
      existingMessages = prior.messages || [];
      sinceTimestamp = existingEntry.lastMessageAt || null;
    } catch (err) {
      logger.info(`  couldn't read existing messages for "${name}" (${err.message}) — treating as fresh`);
    }
  }

  const normalized = [];
  let lastMessageAt = sinceTimestamp;
  let newAttachmentCount = 0;
  let newRecordingCount = 0;
  let newTranscriptCount = 0;

  for await (const msg of listChatMessages(graph, chat.id, { since: sinceTimestamp })) {
    const out = await normalizeMessage({
      graph, handle, chatId: chat.id, msg, opts, logger, stats,
    });
    if (!out) continue;
    normalized.push(out);
    if (out.timestamp && (!lastMessageAt || out.timestamp > lastMessageAt)) lastMessageAt = out.timestamp;
    newAttachmentCount += out.attachments?.filter((a) => a.localPath).length || 0;
    if (out.event?.kind === 'recording' && out.event.localPath) newRecordingCount++;
    if (out.event?.kind === 'transcript' && out.event.localPath) newTranscriptCount++;
  }

  // Merge: existing + new, dedup by id (new wins in case of overlap), then
  // sort ascending. Additive-only — we never drop an archived message.
  const byId = new Map();
  for (const m of existingMessages) byId.set(m.id, m);
  for (const m of normalized) byId.set(m.id, m);
  const merged = [...byId.values()].sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
  const messageCount = merged.length;
  // Derive lastMessageAt from the merged data itself, not the loop variable.
  // This keeps the manifest entry consistent with what's actually on disk,
  // even if a prior run left the field stale.
  lastMessageAt = merged.length ? (merged[merged.length - 1].timestamp || lastMessageAt) : lastMessageAt;
  const attachmentCount = (existingEntry?.attachmentCount || 0) + newAttachmentCount;
  const recordingCount = (existingEntry?.recordingCount || 0) + newRecordingCount;
  const transcriptCount = (existingEntry?.transcriptCount || 0) + newTranscriptCount;

  const payload = {
    id: chat.id,
    kind,
    chatType: chat.chatType,
    name,
    topic: chat.topic || null,
    createdDateTime: chat.createdDateTime || null,
    members: (chat.members || []).map((m) => ({
      displayName: m.displayName || null,
      email: m.email || null,
      userId: m.userId || null,
    })),
    exportedAt: new Date().toISOString(),
    messageCount,
    messages: merged,
  };

  const { messagesJson, conversationJs } = await handle.writeConversation(payload);
  if (existingEntry) {
    logger.info(`   ↳ +${normalized.length} new msgs (${messageCount} total), +${newAttachmentCount} attachments, +${newRecordingCount} recs`);
  } else {
    logger.info(`   ↳ ${messageCount} msgs, ${newAttachmentCount} attachments, ${newRecordingCount} recs, ${newTranscriptCount} transcripts`);
  }

  return {
    id: chat.id,
    kind,
    name,
    slug: handle.slug,
    relDir: handle.relDir,
    conversationJs,
    messagesJson,
    messageCount,
    attachmentCount,
    recordingCount,
    transcriptCount,
    lastMessageAt,
  };
}

async function exportOneChannel({ graph, sink, team, teamSlug, channel, opts, logger }) {
  const name = channel.displayName || channel.id;
  const handle = await sink.openConversation({
    kind: 'channels',
    id: channel.id,
    name,
    teamSlug,
  });

  const normalized = [];
  let lastMessageAt = null;
  let messageCount = 0;

  for await (const msg of listChannelMessages(graph, team.id, channel.id)) {
    // channel messages don't support the /me/chats-scoped hostedContents path;
    // we'll normalize with just body+attachments and skip inline-image downloads.
    const out = await normalizeMessage({
      graph, handle, chatId: null, msg, opts: { ...opts, downloadInlineImages: false }, logger,
    });
    if (!out) continue;
    normalized.push(out);
    messageCount++;
    if (out.timestamp && (!lastMessageAt || out.timestamp > lastMessageAt)) lastMessageAt = out.timestamp;
  }

  const payload = {
    id: channel.id,
    kind: 'channels',
    team: { id: team.id, displayName: team.displayName || null },
    channel: { id: channel.id, displayName: name, description: channel.description || null },
    name: `${team.displayName || 'Team'} › ${name}`,
    exportedAt: new Date().toISOString(),
    messageCount,
    messages: normalized,
  };

  const { messagesJson, conversationJs } = await handle.writeConversation(payload);
  logger.info(`     ↳ ${messageCount} channel msgs`);

  return {
    id: channel.id,
    kind: 'channels',
    name: payload.name,
    slug: handle.slug,
    relDir: handle.relDir,
    conversationJs,
    messagesJson,
    messageCount,
    lastMessageAt,
  };
}

async function normalizeMessage({ graph, handle, chatId, msg, opts, logger, stats }) {
  const timestamp = msg.createdDateTime || null;
  const authorName = msg.from?.user?.displayName
    || msg.from?.application?.displayName
    || msg.from?.device?.displayName
    || (msg.messageType === 'systemEventMessage' ? 'System' : 'Unknown');
  const author = {
    name: authorName,
    id: msg.from?.user?.id || null,
    kind: msg.from?.user ? 'user' : msg.from?.application ? 'app' : msg.from?.device ? 'device' : 'system',
  };

  let bodyHtml = msg.body?.content || '';
  const bodyType = msg.body?.contentType || 'text';

  // Inline images (hosted contents) — parallelized per-message.
  const hostedMap = new Map();
  if (opts.downloadInlineImages && chatId && bodyType === 'html') {
    const ids = extractHostedContentIds(bodyHtml);
    await Promise.all(ids.map(async (hcId) => {
      try {
        const dl = await downloadHostedContent(graph, chatId, msg.id, hcId);
        const ext = extFromContentType(dl.contentType) || '.bin';
        const filename = `inline-${shortId(msg.id)}-${shortId(hcId, 8)}${ext}`;
        const rel = await handle.writeAttachment({
          id: `${msg.id}:${hcId}`,
          filename,
          stream: dl.stream,
          kind: 'inline',
        });
        hostedMap.set(hcId, `attachments/${rel.split('/').pop()}`);
      } catch (err) {
        if (err.status !== 404) logger.warn(`inline image ${hcId} failed: ${err.message}`);
      }
    }));
    if (hostedMap.size) bodyHtml = rewriteBodyHtml(bodyHtml, hostedMap);
  }

  // File-reference attachments — parallelized per-message.
  const attachments = await Promise.all((msg.attachments || []).map(async (att) => {
    const entry = {
      id: att.id,
      name: att.name || null,
      contentType: att.contentType || null,
      contentUrl: att.contentUrl || null,
      thumbnailUrl: att.thumbnailUrl || null,
      localPath: null,
    };
    if (opts.downloadAttachments && isDownloadable(att)) {
      try {
        const dl = await downloadAttachment(graph, att);
        const filename = safeFilename(att.name || dl.filename || `attachment-${shortId(att.id)}`);
        const rel = await handle.writeAttachment({
          id: att.id,
          filename: `${shortId(msg.id)}-${filename}`,
          stream: dl.stream,
          kind: 'file',
        });
        entry.localPath = rel;
      } catch (err) {
        if (err.status === 403 || err.status === 404) {
          if (stats) stats.skippedAttachments403 += 1;
          logger.info(`  attachment "${att.name}" not accessible — skipping`);
        } else {
          logger.warn(`attachment "${att.name}" failed: ${err.message}`);
        }
      }
    }
    return entry;
  }));

  // Recording / transcript event messages — download in parallel.
  let event = null;
  const eventInfo = classifyEventMessage(msg);
  if (eventInfo) {
    event = { ...eventInfo, localPath: null, transcriptLocalPath: null };
    if (eventInfo.kind === 'recording' && opts.downloadRecordings && eventInfo.url) {
      const recPromise = (async () => {
        try {
          const dl = await downloadRecording(graph, eventInfo);
          if (dl) {
            const ext = extFromContentType(dl.contentType) || '.mp4';
            const filename = safeFilename(`${eventInfo.displayName || 'recording'}-${shortId(msg.id)}${ext}`);
            event.localPath = await handle.writeAttachment({
              id: `${msg.id}:recording`,
              filename,
              stream: dl.stream,
              kind: 'recording',
            });
          }
        } catch (err) {
          logger.warn(`recording download failed: ${err.message}`);
        }
      })();
      const transPromise = (opts.downloadTranscripts && eventInfo.url) ? (async () => {
        try {
          const dl = await tryDownloadTranscriptForRecording(graph, eventInfo.url);
          if (dl) {
            const filename = safeFilename(`${eventInfo.displayName || 'recording'}-${shortId(msg.id)}.vtt`);
            event.transcriptLocalPath = await handle.writeAttachment({
              id: `${msg.id}:transcript`,
              filename,
              stream: dl.stream,
              kind: 'transcript',
            });
          }
        } catch (err) {
          logger.warn(`transcript sidecar download failed: ${err.message}`);
        }
      })() : Promise.resolve();
      await Promise.all([recPromise, transPromise]);
    }
  }

  return {
    id: msg.id,
    timestamp,
    lastEditedDateTime: msg.lastEditedDateTime || null,
    deletedDateTime: msg.deletedDateTime || null,
    messageType: msg.messageType || 'message',
    importance: msg.importance || null,
    subject: msg.subject || null,
    author,
    body: { contentType: bodyType, content: bodyHtml },
    attachments,
    reactions: (msg.reactions || []).map((r) => ({
      reactionType: r.reactionType,
      createdDateTime: r.createdDateTime,
      user: r.user?.user?.displayName || null,
    })),
    mentions: (msg.mentions || []).map((m) => ({
      id: m.id,
      mentionText: m.mentionText,
      mentioned: m.mentioned?.user?.displayName || m.mentioned?.tag?.displayName || null,
    })),
    replyToId: msg.replyToId || null,
    event,
  };
}

function extFromContentType(ct) {
  if (!ct) return null;
  const t = ct.split(';')[0].trim().toLowerCase();
  const map = {
    'image/png': '.png',
    'image/jpeg': '.jpg',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/svg+xml': '.svg',
    'video/mp4': '.mp4',
    'video/webm': '.webm',
    'audio/mp4': '.m4a',
    'audio/mpeg': '.mp3',
    'text/vtt': '.vtt',
    'text/plain': '.txt',
    'application/pdf': '.pdf',
    'application/zip': '.zip',
    'application/json': '.json',
  };
  return map[t] || null;
}

// Bounded worker pool. Runs `worker(item, index)` with at most `concurrency`
// promises in flight. Preserves index in the callback so progress can reflect
// the original order.
async function pool(items, concurrency, worker) {
  let next = 0;
  const workers = new Array(Math.min(concurrency, items.length)).fill(null).map(async () => {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      await worker(items[i], i);
    }
  });
  await Promise.all(workers);
}
